import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { speech } from "@/lib/speech";

const MAX_MS = 30000;
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

export const VoiceButton = ({ s, lang, disabled, onText }) => {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const timer = useRef(null);
  const supported = typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  useEffect(() => () => { clearTimeout(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = async (blob, mime) => {
    setState("busy");
    try {
      const fd = new FormData();
      fd.append("audio", blob, mime.includes("mp4") ? "voice.mp4" : "voice.webm");
      fd.append("lang", lang);
      const { data } = await http.post("/voice/transcribe", fd);
      if (data.text) onText(data.text);
      else onText(null, s.micError);
    } catch {
      onText(null, s.micError);
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    speech.unlock();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = r.mimeType || mime || "audio/webm";
        upload(new Blob(chunks, { type }), type);
      };
      r.start();
      rec.current = r;
      setState("rec");
      timer.current = setTimeout(() => r.state === "recording" && r.stop(), MAX_MS);
    } catch {
      onText(null, s.micError);
    }
  };

  const stop = () => {
    clearTimeout(timer.current);
    if (rec.current?.state === "recording") rec.current.stop();
  };

  if (!supported) return null;
  const rec_ = state === "rec";
  return (
    <button type="button" onClick={rec_ ? stop : start} disabled={disabled || state === "busy"} data-testid="chat-mic-button"
      aria-label={rec_ ? s.micStop : s.mic} aria-pressed={rec_}
      className={`relative flex h-[42px] w-[42px] shrink-0 items-center justify-center border-2 transition-colors disabled:opacity-40 ${rec_ ? "border-hv-orange bg-hv-orange text-hv-navy" : "border-hv-navy bg-white text-hv-navy hover:bg-hv-section"}`}>
      {state === "busy" ? <Loader2 size={18} className="animate-spin" /> : rec_ ? <Square size={14} fill="currentColor" /> : <Mic size={18} />}
      {rec_ && <span className="ceres-mic-ring absolute inset-0" aria-hidden="true" />}
    </button>
  );
};
